import React from "react";
import { useTranslation } from "react-i18next";
import ItemOptionsDisplay from "./ItemOptionsDisplay";
import { htmlHasVisibleText, sanitizeMenuHtml } from "./sanitizeMenuHtml";
import { formatCurrency } from "../../../utils/countryLocaleMap";

type MenuItemCardVariant = "menu" | "basket" | "summary";

/**
 * Block containers the calorie note may be placed inside, so it reads as part
 * of the description's closing line rather than dropping below it.
 *
 * Deliberately an allow-list. Anything unrecognised falls back to the
 * description root, which is always safe: a void element (`br`, `img`, `hr`)
 * would drop the note entirely when the document is serialised, and a link or
 * emphasis would keep it but lend it their own semantics.
 */
const NOTE_CONTAINER_TAGS = new Set(["P", "DIV", "LI"]);

/**
 * Resolves where the calorie note belongs: inside the description's closing
 * block of text when it ends in one, otherwise after everything at the root.
 *
 * Appending at the root is always safe, so it is the fallback for any markup
 * this cannot reason about.
 */
function resolveNoteTarget(body: HTMLElement): Element {
  const lastElement = body.lastElementChild;
  if (!lastElement || !NOTE_CONTAINER_TAGS.has(lastElement.tagName)) return body;

  // Text after that element means it is not the end of the description after all.
  const lastMeaningfulNode = Array.from(body.childNodes)
    .reverse()
    .find((node) => node.nodeType === Node.ELEMENT_NODE || !!node.textContent?.trim());

  return lastMeaningfulNode === lastElement ? lastElement : body;
}

/**
 * Appends the calorie note to the end of an item's description.
 *
 * Descriptions are venue-authored HTML, in practice usually a single
 * paragraph, so the note is appended inside that closing block to read as part
 * of the same text. Added as a sibling it would drop onto a line of its own.
 *
 * It is appended as an element built through the DOM API, after the
 * description has been sanitised, so the note cannot introduce markup of its
 * own. Re-serialising preserves the sanitiser's own additions, such as the
 * `rel` it puts on links that open in a new tab.
 */
function appendCaloriesToDescription(sanitisedDescription: string, caloriesNote: string): string {
  if (!caloriesNote) return sanitisedDescription;
  const parsed = new DOMParser().parseFromString(sanitisedDescription, "text/html");
  const note = parsed.createElement("span");
  note.className = "info-text gcw:text-text-steps";
  note.textContent = caloriesNote;
  resolveNoteTarget(parsed.body).append(" ", note);
  return parsed.body.innerHTML;
}

interface ItemDescriptionProps {
  /** Item description (may contain HTML) */
  readonly description?: string;
  /** Energy content in kilocalories, when the venue has configured one */
  readonly calories?: number;
}

/**
 * An item's description with its calorie count appended.
 *
 * When the item has no description the calorie note stands alone, so the
 * information is never lost for items the venue described only by name.
 */
function ItemDescription({ description, calories }: ItemDescriptionProps) {
  const { t } = useTranslation();
  const caloriesNote = calories === undefined ? "" : t("item_calories", "({{calories}}kcal)", { calories });

  if (description && htmlHasVisibleText(description)) {
    return (
      <div
        className="info-text item-description"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Sanitised via sanitizeMenuHtml
        dangerouslySetInnerHTML={{
          __html: appendCaloriesToDescription(sanitizeMenuHtml(description), caloriesNote),
        }}
      />
    );
  }

  if (caloriesNote) {
    return <span className="info-text gcw:text-text-steps">{caloriesNote}</span>;
  }

  return null;
}

interface MenuItemCardProps {
  /** Display variant controlling layout and styling */
  variant: MenuItemCardVariant;
  /** Item name */
  name?: string;
  /** Item description (may contain HTML) */
  description?: string;
  /** Energy content in kilocalories, shown with the description (menu variant) */
  calories?: number;
  /** Item price */
  price?: number;
  /** Allergen strings */
  allergens?: string[];
  /** Allergen info text (pre-formatted, used by menu variant) */
  allergenInfo?: string;
  /** Dietary types for the item */
  dietTypes?: string[];
  /** Person dietary types for highlighting matched diets (menu variant) */
  personDietTypes?: string[];
  /** Warning shown under the item name, e.g. that it contains a guest's allergen (menu and basket variants) */
  warning?: React.ReactNode;
  /** Item configurable options */
  options?: Array<{ label: string; values: Array<{ label: string; price_change?: number }> }>;
  /** Nested items (e.g. sub-items in basket) */
  nestedItems?: Array<{ id: string; name?: string }>;
  /** Whether to hide prices */
  hidePrices?: boolean;
  /** Whether the item is in a fixed-price menu (hides individual price in menu variant) */
  isFixedPrice?: boolean;
  /** Processed discount info for menu variant */
  processedDiscounts?: Array<{ quantity: number; formattedAmount: string }>;
  /** Currency code (e.g. "GBP") */
  currency: string;
  /** Country code (e.g. "GB") */
  countryCode: string;
  /** For-name label (summary variant, e.g. "For John") */
  forName?: string;
  /** Optional CSS class for the container */
  className?: string;
  /** Slot for action controls (e.g. QuantityControls) */
  actions?: React.ReactNode;
  /** Menu-specific discount key prefix */
  discountKeyPrefix?: string;
}

/**
 * Unified card component for rendering menu/basket/summary item rows.
 *
 * - **menu** variant: shows item name, allergen warnings, allergens, diet
 *   types, price, and discount info. Actions slot renders quantity controls.
 * - **basket** variant: shows item name, allergen warnings, allergens, options, nested items,
 *   and price. Actions slot renders quantity controls.
 * - **summary** variant: shows item name, for-name label, allergens,
 *   options, and price. Actions slot renders a quantity badge.
 */
const MenuItemCard: React.FC<MenuItemCardProps> = ({
  variant,
  name,
  description,
  calories,
  price,
  allergens,
  allergenInfo,
  dietTypes,
  personDietTypes = [],
  warning,
  options,
  nestedItems,
  hidePrices = false,
  isFixedPrice = false,
  processedDiscounts,
  currency,
  countryCode,
  forName,
  className,
  actions,
  discountKeyPrefix,
}) => {
  const { t } = useTranslation();

  if (variant === "menu") {
    return (
      <>
        <div className={className ?? "menu-item-container"}>
          <p className="gcw:flex gcw:flex-col item-title">{name ?? ""}</p>
          <ItemDescription description={description} calories={calories} />
          {warning}
          {allergenInfo && (
            <p className="info-text dietary-text">{`${t("Allergens:", "Allergens:")} ${allergenInfo}`}</p>
          )}
          {dietTypes && dietTypes.length > 0 && (
            <p className="info-text dietary-text">
              {`${t("Dietary:", "Dietary:")} `}
              {dietTypes.map((dietType: string, idx: number) => {
                const isSelected = personDietTypes.includes(dietType);
                return (
                  <span key={dietType}>
                    {isSelected ? <strong>{dietType}</strong> : dietType}
                    {idx < dietTypes.length - 1 ? ", " : ""}
                  </span>
                );
              })}
            </p>
          )}
          {!hidePrices && !isFixedPrice && (
            <span className="menu-price-inline gcw:pt-1 gcw:pb-1">
              {formatCurrency(price ?? 0, currency, countryCode)}
            </span>
          )}
          {processedDiscounts?.map((discount, idx: number) => (
            <p className="info-text" key={`${discountKeyPrefix ?? "discount"}-${idx}`}>
              {t("discount_description", {
                quantity: discount.quantity,
                amount: discount.formattedAmount,
              })}
            </p>
          ))}
        </div>
        {actions}
      </>
    );
  }

  if (variant === "summary") {
    return (
      <>
        <div className={className ?? "menu-item-container"}>
          <span className="gcw:flex gcw:flex-col menu-item">{name ?? ""}</span>
          {forName && (
            <span className="info-text for-name-text">{t("for_name", "For {{name}}", { name: forName })}</span>
          )}
          {allergens && allergens.length > 0 && (
            <span className="info-text dietary-text">
              {t("allergens_label", "Allergens: {{allergens}}", {
                allergens: allergens.join(", "),
              })}
            </span>
          )}
          <ItemOptionsDisplay options={options} currency={currency} countryCode={countryCode} hidePrices={hidePrices} />
          {!hidePrices && (
            <span className="menu-price-inline gcw:pt-1 gcw:pb-1">
              {formatCurrency(price ?? 0, currency, countryCode)}
            </span>
          )}
        </div>
        {actions}
      </>
    );
  }

  // basket variant
  return (
    <>
      <div className={className ?? "menu-item-container"}>
        <span className="gcw:flex gcw:flex-col menu-item">{name ?? ""}</span>
        {warning}
        {allergens && allergens.length > 0 && (
          <span className="info-text dietary-text">
            {t("allergens_label", "Allergens: {{allergens}}", {
              allergens: allergens.join(", "),
            })}
          </span>
        )}
        <ItemOptionsDisplay options={options} currency={currency} countryCode={countryCode} hidePrices={hidePrices} />
        {nestedItems && nestedItems.length > 0 && (
          <div className="gcw:flex gcw:flex-col menu-item">
            {nestedItems.map((opt, idx) => (
              <div key={`${opt.id}-${idx}`}>
                <strong className="options-label gcw:pt-2">{opt.name}</strong>
              </div>
            ))}
          </div>
        )}
        {!hidePrices && (
          <span className="menu-price-inline gcw:pt-1 gcw:pb-1">
            {formatCurrency(price ?? 0, currency, countryCode)}
          </span>
        )}
      </div>
      {actions}
    </>
  );
};

export default MenuItemCard;
